import React from 'react';

import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { FormControlComponent } from 'design/formControl';
import { PasswordChangeFieldset } from 'containers/PasswordChangeFieldset';
import { usePasswordChangeForm } from 'hooks/usePasswordChangeForm';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { FieldsetLayoutComponent } from 'design/fieldsetLayout';
import { validatePasswordChangeFormValues } from 'utils/passwordChangeFormUtils';
import { useServices } from 'hooks/useServices';
import { WrongPasswordError } from 'utils/errors';

type Props = {
  onCancel(): void;
  onEdited(): void;
};

export const PartnerAccountManagerPasswordEditor = (props: Props) => {
  const { onCancel: cancel, onEdited } = props;

  const { active: asyncActive, injectPromise } = useAsyncBoundary();

  const { submitPartnerPasswordChangeForm } = useServices();

  const {
    partnerAccountManager: { passwordEditorScreen: ownDictionary },
  } = useIntlDictionary();

  const {
    values: formValues,
    violations: formViolations,
    setViolations: setFormViolations,
    setCurrentPassword,
    setNextPassword,
    setNextPasswordRepeat,
  } = usePasswordChangeForm();

  const submit = React.useCallback(() => {
    setFormViolations([]);

    const violations = validatePasswordChangeFormValues(formValues, {
      shouldValidateCurrentPassword: true,
    });

    if (violations.length === 0) {
      const promise = submitPartnerPasswordChangeForm(null, formValues)
        .then(onEdited)
        .catch((error) => {
          if (error instanceof WrongPasswordError) {
            setFormViolations((prev) => [
              ...prev,
              { name: 'CurrentPasswordDoNotMatch' },
            ]);
          }
        });

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      Promise.resolve(violations).then(setFormViolations);
    }
  }, [
    formValues,
    injectPromise,
    onEdited,
    setFormViolations,
    submitPartnerPasswordChangeForm,
  ]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {ownDictionary.titleText}
          </TextComponent>
        }
        actions={null}
      />
      <FormControlComponent
        shouldFocusFirstInvalidInput={formViolations.length > 0}
        onSubmit={submit}
      >
        <SidebarModalSectionComponent
          content={
            <FieldsetLayoutComponent>
              <PasswordChangeFieldset
                hasCurrentPasswordField
                disabled={asyncActive}
                values={formValues}
                violations={formViolations}
                onCurrentPasswordChange={setCurrentPassword}
                onNextPasswordChange={setNextPassword}
                onNextPasswordRepeatChange={setNextPasswordRepeat}
              />
            </FieldsetLayoutComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {ownDictionary.submitActionText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={cancel}
              >
                {ownDictionary.cancelActionText}
              </ModalPressControlComponent>
            </>
          }
        />
      </FormControlComponent>
    </>
  );
};
